import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { SessionType } from '@shared';
import {
  guidedPlanningQuoteKeys,
  guidedPlanningApi,
} from '@/features/guided-financial-planning/api';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { selectSession, selectFeatureFlags } from '@client/model/selectors';

export function useGuidedPlanningQuotes(
  clientId: string,
  session: GuidedPlanningSessionResponse | undefined,
) {
  const { flags } = useFeatureFlags();
  const { sessionType } = useSelector(selectSession);
  const reduxFeatureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!reduxFeatureFlags.d2cMode;

  const sessionId = session?.id;
  const selectedCategories = session?.stepData?.selectedPolicyCategories;
  const paymentPreference = session?.stepData?.preferences?.paymentPreference;

  // D2C skips the options step, so selectedCategories is empty — the server returns all types
  const hasCategorySelection = isD2C || Boolean(selectedCategories?.length);

  const query = useQuery({
    queryKey: guidedPlanningQuoteKeys.list({
      clientId,
      sessionId,
      selectedCategories,
      paymentPreference,
    }),
    queryFn: () => guidedPlanningApi.getQuotes(clientId, sessionId!),
    enabled: Boolean(
      clientId &&
        sessionId &&
        hasCategorySelection &&
        session?.status !== 'completed',
    ),
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    // Extract quotes array for backward compatibility
    data: flags.nycLifeDemo
      ? query.data?.quotes?.filter(quote => quote?.fundingPolicyType !== 2936)
      : query.data?.quotes,
    // Expose metadata for empty state messaging
    noQuotesReason: query.data?.noQuotesReason,
    // True when preferred policy types had no matches and all quotes are shown instead
    isFallback: query.data?.isFallback ?? false,
    // Expose refetching state for filter changes
    isRefetching: query.isRefetching,
  };
}
